import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CLINIC_LOGO = 'https://customer-assets.emergentagent.com/job_bfad35dd-1d63-4524-ae59-d9c9812969d6/artifacts/e9pbdmoo_B3263100-FCB9-4C56-9C4C-F3DA7CC292D8.PNG';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [onDutyDoctor, setOnDutyDoctor] = useState(null);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      const roleRoutes = { doctor: '/doctor', staff: '/staff', nurse: '/nursing' };
      navigate(roleRoutes[user.role] || `/${user.role}`, { replace: true });
    }
    const fetchOnDutyDoctor = async () => {
      try {
        const response = await axios.get(`${API}/doctors/on-duty`);
        setOnDutyDoctor(response.data);
      } catch (error) {
        console.error('Error fetching on-duty doctor:', error);
      }
    };
    fetchOnDutyDoctor();
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      const { token, user: userData } = response.data;
      login(token, userData);
      toast.success(`Welcome ${userData.role}!`);
      const roleRoutes = { doctor: '/doctor', staff: '/staff', nurse: '/nursing' };
      navigate(roleRoutes[userData.role] || `/${userData.role}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen grid lg:grid-cols-2"
      style={{
        background:
          'radial-gradient(circle at 20% 20%, rgba(30,58,138,0.10), transparent 45%), radial-gradient(circle at 80% 80%, rgba(22,163,74,0.10), transparent 50%), linear-gradient(135deg, #f8fafc 0%, #ecfdf5 100%)',
      }}
    >
      {/* Local keyframes */}
      <style>{`
        @keyframes logoFloat {
          0%, 100% { transform: translateY(0) scale(1); }
          50%      { transform: translateY(-8px) scale(1.015); }
        }
        @keyframes logoRing {
          0%   { transform: rotate(0deg);   opacity: 0.35; }
          50%  { opacity: 0.55; }
          100% { transform: rotate(360deg); opacity: 0.35; }
        }
        .logo-float { animation: logoFloat 6s ease-in-out infinite; }
        .logo-ring  { animation: logoRing 60s linear infinite; }
      `}</style>

      {/* Left side — Branded logo panel */}
      <div className="hidden lg:flex items-center justify-center relative overflow-hidden">
        {/* Decorative rotating ring */}
        <div
          aria-hidden
          className="logo-ring absolute pointer-events-none"
          style={{
            width: '92%', aspectRatio: '1/1',
            border: '1px dashed rgba(30,58,138,0.18)',
            borderRadius: '50%',
            background:
              'conic-gradient(from 0deg, rgba(22,163,74,0.06), rgba(30,58,138,0.10), rgba(22,163,74,0.06))',
          }}
        />
        <img
          src={CLINIC_LOGO}
          alt="K.M Multi Speciality Clinic"
          className="logo-float relative drop-shadow-2xl"
          style={{ maxWidth: '78%', maxHeight: '78%', objectFit: 'contain', mixBlendMode: 'multiply' }}
        />
      </div>

      {/* Right side — Login Form */}
      <div className="flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md space-y-8">
          {/* Logo on mobile (left panel is hidden on small screens) */}
          <div className="text-center space-y-4">
            <img
              src={CLINIC_LOGO}
              alt="K.M Multi Speciality Clinic"
              className="mx-auto h-32 sm:h-40 lg:hidden"
              style={{ objectFit: 'contain', mixBlendMode: 'multiply' }}
            />
            <div className="space-y-1">
              <h1
                className="text-4xl sm:text-5xl font-black tracking-tight bg-clip-text text-transparent"
                style={{
                  fontFamily: 'Manrope, sans-serif',
                  backgroundImage: 'linear-gradient(135deg, #1e3a8a 0%, #16a34a 100%)',
                }}
              >
                K.M Multi-Speciality Clinic
              </h1>
              <p
                className="text-sm font-semibold tracking-wide"
                style={{ color: '#16a34a', fontFamily: 'Manrope, sans-serif' }}
              >
                — Care Beyond Treatment —
              </p>
              <p className="text-base text-slate-500 font-medium pt-1">Koliyanur</p>
            </div>
            <p className="text-sm text-slate-500 pt-2">Sign in to manage clinic queue</p>
          </div>

          {/* On-duty doctor card */}
          {onDutyDoctor && (
            <div
              className="flex items-center gap-3 rounded-xl p-3 border"
              style={{
                background: 'linear-gradient(135deg, #eff6ff 0%, #ecfdf5 100%)',
                borderColor: 'rgba(30,58,138,0.15)',
              }}
            >
              {onDutyDoctor.photo_url ? (
                <img
                  src={onDutyDoctor.photo_url}
                  alt={onDutyDoctor.name}
                  className="h-12 w-12 rounded-full object-cover border-2 border-blue-200"
                />
              ) : (
                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center text-2xl">👨‍⚕️</div>
              )}
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">On Duty</div>
                <div className="font-bold text-blue-900 truncate">{onDutyDoctor.name}</div>
                {onDutyDoctor.qualification && (
                  <div className="text-xs text-slate-500 truncate">{onDutyDoctor.qualification}</div>
                )}
              </div>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="username" data-testid="username-label">Username</Label>
              <Input
                id="username"
                data-testid="username-input"
                type="text"
                placeholder="Enter username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" data-testid="password-label">Password</Label>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11"
              />
            </div>
            <Button
              type="submit"
              data-testid="login-button"
              className="w-full h-11 text-white font-bold tracking-wide"
              disabled={loading}
              style={{
                background: 'linear-gradient(135deg, #1e3a8a 0%, #16a34a 100%)',
                boxShadow: '0 4px 14px rgba(30,58,138,0.35)',
              }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
